import React, { useState, useEffect } from 'react';
import accessLogsService from '../../../services/accessLogsService';
import LogTable from './LogTable';
import FilterPanel from './FilterPanel';

const PermissionChangesTab = ({ darkMode }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 50,
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    const handleRefresh = () => {
      loadData();
    };
    window.addEventListener('accessLogsRefresh', handleRefresh);
    return () => window.removeEventListener('accessLogsRefresh', handleRefresh);
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const logsData = await accessLogsService.getPermissionChanges(filters);
      setLogs(logsData.logs || []);
      setTotalCount(logsData.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to load permission changes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleSort = (column, order) => {
    setFilters({ ...filters, sortBy: column, sortOrder: order, page: 1 });
  };

  const columns = [
    { key: 'timestamp', label: 'Timestamp', sortable: true },
    { key: 'admin', label: 'Admin', sortable: false },
    { key: 'affectedUser', label: 'Affected User', sortable: false },
    { key: 'changeType', label: 'Change Type', sortable: false },
    { key: 'previousValue', label: 'Previous', sortable: false },
    { key: 'newValue', label: 'New', sortable: false },
    { key: 'reason', label: 'Reason', sortable: false },
    { key: 'approvalStatus', label: 'Status', sortable: false }
  ];

  const formatLogRow = (log) => {
    let previousValue = 'N/A';
    let newValue = 'N/A';
    try {
      if (log.previousValue) {
        const prev = JSON.parse(log.previousValue);
        previousValue = prev.RoleName || JSON.stringify(prev);
      }
      if (log.newValue) {
        const next = JSON.parse(log.newValue);
        newValue = next.RoleName || JSON.stringify(next);
      }
    } catch {}

    return {
      id: log.id,
      timestamp: new Date(log.timestamp).toLocaleString(),
      admin: log.admin ? `${log.admin.firstName || ''} ${log.admin.lastName || ''}`.trim() || log.admin.email : 'N/A',
      affectedUser: log.affectedUser ? `${log.affectedUser.firstName || ''} ${log.affectedUser.lastName || ''}`.trim() || log.affectedUser.email : 'N/A',
      changeType: log.changeType || 'N/A',
      previousValue,
      newValue,
      reason: log.reason || 'N/A',
      approvalStatus: log.approvalStatus || 'N/A',
      severityLevel: log.severityLevel
    };
  };

  return (
    <div className={`access-logs-tab-content ${darkMode ? 'dark-mode' : ''}`}>
      <FilterPanel filters={filters} onChange={handleFilterChange} darkMode={darkMode} />
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadData}>Retry</button>
        </div>
      )}
      <LogTable
        columns={columns}
        data={logs.map(formatLogRow)}
        loading={loading}
        pagination={{
          currentPage: filters.page,
          pageSize: filters.pageSize,
          totalCount: totalCount,
          onPageChange: handlePageChange
        }}
        sorting={{
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          onSort: handleSort
        }}
        darkMode={darkMode}
      />
    </div>
  );
};

export default PermissionChangesTab;

